
import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { CircularProgress, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '../../hooks/useUserRoles';
import { useSudo } from '../../contexts/SudoContext';

/**
 * AdminGuard enforces both 'super_admin' role check AND secondary 'Sudo' authentication.
 * It renders the child routes (Outlet) only if both checks pass.
 */
const AdminGuard: React.FC = () => {
    const { t } = useTranslation();
    const { can, isRolesLoading } = useUserRoles();
    const { requestSudo, isSudoActive } = useSudo();
    const navigate = useNavigate();
    const [isCheckingSudo, setIsCheckingSudo] = useState(true);

    useEffect(() => {
        const verifyAccess = async () => {
            if (!isRolesLoading) {
                if (can('super_admin')) {
                    if (!isSudoActive) {
                        const success = await requestSudo();
                        if (!success) {
                            navigate('/'); // Redirect home if sudo fails/cancels
                            return;
                        }
                    }
                }
                setIsCheckingSudo(false);
            }
        };

        verifyAccess();
    }, [isRolesLoading, can, isSudoActive, requestSudo, navigate]);

    if (isRolesLoading || (can('super_admin') && isCheckingSudo)) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!can('super_admin')) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" color="error">{t('accessDenied.title')}</Typography>
                <Typography>{t('accessDenied.message')}</Typography>
            </Container>
        );
    }

    // If we passed both checks, render the nested route content
    return <Outlet />;
};

export default AdminGuard;
